import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { weeklyOperatingHoursFormSchema, type WeeklyOperatingHoursFormData } from '@geely/shared/schemas';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@geely/ui";
import type { WeeklyOperatingHours, Store } from '@geely/shared/types';
import { DAY_OF_WEEK_LABELS } from '@geely/shared/constants';

interface WeeklyHoursFormDialogProps {
  isOpen: boolean;
  editingHours: WeeklyOperatingHours | null;
  userStore: Store | null;
  onSubmit: (data: WeeklyOperatingHoursFormData) => Promise<void>;
  onClose: () => void;
}

const defaultValues: Partial<WeeklyOperatingHoursFormData> = {
  dayOfWeek: 1,
  isClosed: false,
  startTime: '',
  endTime: '',
  timeInterval: 30,
  hasLunchBreak: false,
  lunchStartTime: '',
  lunchEndTime: '',
  parallelAppointments: undefined,
  isActive: true,
};

export function WeeklyHoursFormDialog({
  isOpen,
  editingHours,
  userStore,
  onSubmit,
  onClose,
}: WeeklyHoursFormDialogProps) {
  const form = useForm<WeeklyOperatingHoursFormData>({
    resolver: zodResolver(weeklyOperatingHoursFormSchema),
    defaultValues,
    mode: 'onSubmit',
  });

  const { formState: { isSubmitting }, watch } = form;
  const isClosed = watch('isClosed');
  const hasLunchBreak = watch('hasLunchBreak');

  useEffect(() => {
    if (isOpen) {
      if (editingHours) {
        form.reset({
          dayOfWeek: editingHours.dayOfWeek,
          isClosed: editingHours.isClosed,
          startTime: editingHours.startTime || '',
          endTime: editingHours.endTime || '',
          timeInterval: editingHours.timeInterval || 30,
          hasLunchBreak: editingHours.hasLunchBreak,
          lunchStartTime: editingHours.lunchStartTime || '',
          lunchEndTime: editingHours.lunchEndTime || '',
          parallelAppointments: editingHours.parallelAppointments ?? undefined,
          isActive: editingHours.isActive,
        });
      } else {
        form.reset(defaultValues);
      }
    }
  }, [isOpen, editingHours, form]);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingHours ? 'Editar Horário Semanal' : 'Novo Horário Semanal'}
          </DialogTitle>
          <DialogDescription>
            Configure o horário de funcionamento para um dia da semana
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="space-y-4 py-4">
              <FormField
                control={form.control}
                name="dayOfWeek"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dia da Semana *</FormLabel>
                    <Select
                      value={field.value?.toString()}
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      disabled={!!editingHours}
                    >
                      <FormControl>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecione o dia" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(DAY_OF_WEEK_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isClosed"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2 rounded-md border p-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="!mt-0">
                      <FormLabel className="!mt-0 font-medium">Loja fechada neste dia</FormLabel>
                      <p className="text-xs text-gray-500 mt-1">
                        Marque se a loja não funciona neste dia da semana
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              {!isClosed && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="startTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Horário Início *</FormLabel>
                          <FormControl>
                            <Input type="time" className="mt-1" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="endTime"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Horário Fim *</FormLabel>
                          <FormControl>
                            <Input type="time" className="mt-1" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="timeInterval"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Intervalo (minutos) *</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        >
                          <FormControl>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Selecione o intervalo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="15">15 minutos</SelectItem>
                            <SelectItem value="30">30 minutos</SelectItem>
                            <SelectItem value="45">45 minutos</SelectItem>
                            <SelectItem value="60">60 minutos</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="hasLunchBreak"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2 rounded-md border p-3">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="!mt-0">
                          <FormLabel className="!mt-0 font-medium">Possui intervalo de almoço</FormLabel>
                          <p className="text-xs text-gray-500 mt-1">
                            Período onde agendamentos não podem ser marcados
                          </p>
                        </div>
                      </FormItem>
                    )}
                  />

                  {hasLunchBreak && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="lunchStartTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Início Almoço *</FormLabel>
                            <FormControl>
                              <Input type="time" className="mt-1" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lunchEndTime"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Fim Almoço *</FormLabel>
                            <FormControl>
                              <Input type="time" className="mt-1" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="parallelAppointments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Atendimentos Simultâneos (opcional)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min="1"
                            max="10"
                            placeholder={`Padrão da loja: ${userStore?.parallelAppointments || 1}`}
                            className="mt-1"
                            {...field}
                            value={field.value ?? ''}
                            onChange={(e) => field.onChange(e.target.value === '' ? undefined : parseInt(e.target.value))}
                          />
                        </FormControl>
                        <p className="text-xs text-gray-500 mt-1">
                          Deixe vazio para usar o padrão da loja ({userStore?.parallelAppointments})
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0">Horário ativo</FormLabel>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Carregando...' : (editingHours ? 'Atualizar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
